import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';

function Overview() {
  const [stats, setStats] = useState({});
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [showClientInfo, setShowClientInfo] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchTodayAppointments();
  }, []);

  const fetchStats = async () => {
    const response = await axios.get(`${API_URL}/dashboard/stats`);
    setStats(response.data);
  };

  const fetchTodayAppointments = async () => {
    // Get today's date in local timezone (not UTC)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const today = `${year}-${month}-${day}`;

    const response = await axios.get(`${API_URL}/appointments/by-date/${today}`);

    // Fetch full appointment details with client and service names
    const allAppointments = await axios.get(`${API_URL}/appointments`);
    const todayAppts = allAppointments.data.filter(apt => apt.appointment_date === today);

    // Sort by time (earliest to latest)
    todayAppts.sort((a, b) => {
      const timeA = a.appointment_time.split(':').map(Number);
      const timeB = b.appointment_time.split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });

    setTodayAppointments(todayAppts);
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const viewClientInfo = async (clientId) => {
    // Fetch client details
    const clientResponse = await axios.get(`${API_URL}/clients`);
    const client = clientResponse.data.find(c => c.id === clientId);

    // Fetch client's appointment history (last 5)
    const historyResponse = await axios.get(`${API_URL}/clients/${clientId}/history`);
    const history = historyResponse.data.slice(0, 5); // Get last 5 appointments

    setSelectedClient(client);
    setClientHistory(history);
    setShowClientInfo(true);
  };

  return (
    <div className="overview">
      <h2>Dashboard Overview</h2>
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{stats.totalClients || 0}</h3>
          <p>Total Clients</p>
        </div>
        <div className="stat-card">
          <h3>{stats.todayAppointments || 0}</h3>
          <p>Today's Appointments</p>
        </div>
        <div className="stat-card">
          <h3>{stats.upcomingAppointments || 0}</h3>
          <p>Upcoming Appointments</p>
        </div>
        <div className="stat-card alert">
          <h3>{stats.lowStockItems || 0}</h3>
          <p>Low Stock Items</p>
        </div>
      </div>

      <div className="today-appointments-section">
        <h3>Today's Appointments</h3>
        {todayAppointments.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Client</th>
                <th>Phone</th>
                <th>Service</th>
                <th>Duration</th>
                <th>Price</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {todayAppointments.map(apt => {
                // Handle multiple services display
                let serviceDisplay = apt.service_name;
                let durationDisplay = apt.service_duration;
                let priceDisplay = apt.service_price;

                if (apt.services && apt.services.length > 0) {
                  if (apt.services.length === 1) {
                    serviceDisplay = apt.services[0].name;
                  } else {
                    serviceDisplay = apt.services.map(s => s.name).join(', ');
                  }
                  durationDisplay = apt.total_duration || apt.service_duration;
                  priceDisplay = apt.total_price || apt.service_price;
                }

                return (
                  <tr key={apt.id}>
                    <td><strong>{formatTime(apt.appointment_time)}</strong></td>
                    <td>
                      <span
                        className="client-link"
                        onClick={() => viewClientInfo(apt.client_id)}
                      >
                        {apt.client_name}
                      </span>
                    </td>
                    <td>{apt.client_phone}</td>
                    <td>{serviceDisplay}</td>
                    <td>{durationDisplay} min</td>
                    <td>${priceDisplay}</td>
                    <td>{apt.notes}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="no-results">No appointments scheduled for today.</p>
        )}
      </div>

      {showClientInfo && selectedClient && (
        <div className="modal">
          <div className="modal-content" style={{ maxWidth: '800px' }}>
            <h3>Client Information</h3>
            <div className="client-info-details">
              <p><strong>Name:</strong> {selectedClient.name}</p>
              <p><strong>Email:</strong> {selectedClient.email}</p>
              <p><strong>Phone:</strong> {selectedClient.phone}</p>

              {selectedClient.notes && (
                <div className="client-notes-section">
                  <p><strong>General Notes:</strong></p>
                  <p className="notes-content">{selectedClient.notes}</p>
                </div>
              )}

              {selectedClient.formula_notes && (
                <div className="client-notes-section">
                  <p><strong>Formula Notes:</strong></p>
                  <p className="notes-content">{selectedClient.formula_notes}</p>
                </div>
              )}

              <div className="client-history-section">
                <h4>Recent Service History</h4>
                {clientHistory.length > 0 ? (
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Service</th>
                        <th>Stylist</th>
                        <th>Duration</th>
                        <th>Price</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientHistory.map(apt => {
                        // Handle multiple services display
                        let serviceDisplay = apt.service_name;
                        let durationDisplay = apt.service_duration;
                        let priceDisplay = apt.service_price;

                        if (apt.services && apt.services.length > 0) {
                          if (apt.services.length === 1) {
                            serviceDisplay = apt.services[0].name;
                          } else {
                            serviceDisplay = apt.services.map(s => s.name).join(', ');
                          }
                          durationDisplay = apt.total_duration || apt.service_duration;
                          priceDisplay = apt.total_price || apt.service_price;
                        }

                        return (
                          <tr key={apt.id}>
                            <td>{formatDate(apt.appointment_date)}</td>
                            <td>{serviceDisplay}</td>
                            <td>{apt.stylist_name || '-'}</td>
                            <td>{durationDisplay} min</td>
                            <td>${priceDisplay}</td>
                            <td>{apt.notes || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-results">No service history available.</p>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => {
                setShowClientInfo(false);
                setSelectedClient(null);
                setClientHistory([]);
              }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Overview;
